import { useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "";

/**
 * useAnalytics — SPA route-change page_view tracker for GA4.
 *
 * gtag itself is loaded in `public/index.html` behind an env-gate + opt-out
 * check. This hook fires ONE `page_view` per route change so the SPA is not
 * counted as a single-page session forever.
 *
 * Mounted once in <Layout>. Safe to no-op when gtag was never loaded
 * (missing GA_ID, opt-out set, or localStorage disabled).
 */
export function useAnalytics() {
  const location = useLocation();

  useEffect(() => {
    if (!GA_ID) return;
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    // Respect runtime opt-out. `regulens_analytics_optout=1` disables sending.
    try {
      if (localStorage.getItem("regulens_analytics_optout") === "1") return;
    } catch (e) { /* fall through — ok to send if localStorage isn't available */ }

    window.gtag("event", "page_view", {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);
}

/**
 * Runtime opt-out toggle used by the notice banner + the Privacy Policy page.
 *   optOut(true)  → sets `window["ga-disable-<ID>"] = true`, sets localStorage
 *                   flag, and future page_view events no-op.
 *   optOut(false) → clears the flag. Reload required for gtag script to
 *                   actually load if it was never loaded — this is the
 *                   documented Google behavior (see the `ga-disable-*` flag).
 */
export function useAnalyticsOptOut() {
  const setOptOut = useCallback((optOut) => {
    if (!GA_ID) return;
    try {
      if (optOut) {
        localStorage.setItem("regulens_analytics_optout", "1");
        window["ga-disable-" + GA_ID] = true;
      } else {
        localStorage.removeItem("regulens_analytics_optout");
        window["ga-disable-" + GA_ID] = false;
      }
    } catch (e) { /* localStorage disabled — no-op */ }
  }, []);

  const isOptedOut = useCallback(() => {
    try {
      return localStorage.getItem("regulens_analytics_optout") === "1";
    } catch (e) {
      return false;
    }
  }, []);

  return { setOptOut, isOptedOut, gaEnabled: !!GA_ID };
}
